import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Covalent Spotlights Use Cases with DEX Data\ndate: 2022-04-19T00:25:15-08:00\ncategories: [\"product\"]\ndescription: Covalent Releases New DEX Use Case Guide!\nauthor: leilani\nsubbrand_image: https://www.covalenthq.com/static/images/blog/202204/xyk1.jpg\ntype: legacy\n---\n\n\n\n## TL;DR\n\n- Covalent releases a new use case [webpage](https://www.covalenthq.com/use-cases/dex/) focused on its leading Decentralized Exchange (DEX) endpoints and their applications\n- This is the first of many curated use case pages to onboard new and experienced developers into Web3\n- The use case webpage builds on the December [release](https://www.covalenthq.com/blog/xyk-announcement/) of the unified `XY=K` [endpoints](https://www.covalenthq.com/docs/api/#/0/Get%20XY=K%20supported%20DEXes/USD/1) and rise in multi-chain DEX analytics\n- Coming soon: a **customizable ticker widget** to display top token and pool statistics for supported [DEXs](https://www.covalenthq.com/docs/api/#/0/Get%20XY=K%20supported%20DEXes/USD/1)\n\n\n## Covalent and DEX Data\n\nDecentralized Finance (DeFi) is responsible for facilitating billions of dollars of trades on different blockchains. With much of this activity taking place on DEXs, there is a vast amount of data stored on-chain regarding swaps, staking, liquidity, and more. With so many different protocols, it can be extremely difficult for developers to aggregate data across different platforms, and for the average DeFi user to keep track of their assets. \n\nHowever, the majority of popular exchanges have something in common, which is they operate as clones of the UniswapV2 protocol (read more about this [here](https://www.covalenthq.com/blog/xyk-announcement/)). Covalent’s strength is in **standardizing data**, and finding shortcuts for developers is one of the many ways we improve the Web3 experience. With our Unified API, we found that we could easily support similar exchanges all through a set of `XY=K` endpoints, in a standardized response format across different chains. Check out this [long-form article](https://medium.com/covalent-hq/the-developers-complete-guide-to-dex-data-using-covalent-70e522157365) for a **deep dive** on our DEX support. \n\nThis easy multi-chain functionality doesn’t exist elsewhere in the market, and it’s propelled us to think about many more types of applications.\n\n\n![Image](https://www.covalenthq.com/static/images/blog/202204/use-case page.png)\n\n\n\n## Taking a Use Case Approach \n\nBy focusing on data trends in the Web3 space, it has become clear that there are various tracks for developers to build in according to user demographics, such as NFTs (Non-Fungible Tokens), DAOs (Decentralized Autonomous Organizations), DEXs and more. Each of these areas have their own learning curves and data requirements and is why we have started to categorize our endpoints to make it easier for developers. \n\nIn order to improve developer onboarding, we are creating use case pages on our website to collect all the necessary resources in one place, making it easy for both experienced devs and Web3 newbies.\n\n\n## What’s Next?\n\nAs we continue to expand our developer resources, we are shipping easy-to-use **code templates**. On the topic of DEXs, we are building a customizable widget for anyone to display top tokens or pair statistics on any of the 18 *(and counting)* DEXs we currently support.\n\n\n![Image](https://www.covalenthq.com/static/images/blog/202204/Light Expanded.png)\n\n\nThis widget brings some of the TradFi (Traditional Finance) experience to the Web3 ecosystem, with a similar aesthetic to stock market tickers which many investors are familiar with. Customize and embed the widget to consolidate top DEX data on any website!\n\nTo check out our other public code templates, including a fork-able [DEX analytics dashboard](https://covalenthq.github.io/dex-dashboard-template/#/), head to our **[GitHub](https://covalenthq.github.io/dex-dashboard-template/#/)** or **[Replit](https://replit.com/@Covalent-Templates)**!\n\n**Developers and the Covalent Network**\nAPI usage is important for us not only because it means we are expanding our community, but it also benefits the health of the Covalent Network. For holders of our **[Covalent Query Token](https://etherscan.io/token/0xD417144312DbF50465b1C641d016962017Ef6240) (CQT)**, demand-side traction contributes to the value accrual of the token. Stay tuned for more information on the upcoming launch of single-sided staking.\n\n\n\n\n";
const filepath = "/blog/dex-use-case-page.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
